import React from 'react';
import { reportError } from '@/Services/errorReporter';

interface AppErrorBoundaryProps {
    children: React.ReactNode;
}

interface AppErrorBoundaryState {
    hasError: boolean;
    message: string;
}

class AppErrorBoundary extends React.Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
    constructor(props: AppErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, message: '' };
    }

    static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
        return {
            hasError: true,
            message: error?.message || 'Erreur inattendue',
        };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        reportError({
            message: error?.message || 'Unknown React error',
            source: 'react',
            url: window.location.href,
            component: info?.componentStack ? info.componentStack.trim().split('\n')[0].trim() : null,
            stack: error?.stack || null,
        });
    }

    render(): React.ReactNode {
        if (this.state.hasError) {
            return (
                <div className="min-h-[40vh] flex items-center justify-center px-4">
                    <div className="flex flex-col items-center text-center gap-4 bg-app-surface dark:bg-gray-800 border border-app-border rounded-2xl p-8 max-w-sm shadow-sm">
                        <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                            <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="12" y1="8" x2="12" y2="12" />
                                <line x1="12" y1="16" x2="12.01" y2="16" />
                            </svg>
                        </div>
                        <h2 className="text-lg font-bold text-app-text mb-1">Une erreur est survenue</h2>
                        <p className="text-sm text-app-muted">{this.state.message}</p>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default AppErrorBoundary;
